import { LoadingSpinner } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import { SmsIntegrationModel } from "../types";
import useTeams from "@/hooks/useTeams";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";

interface RcsBusinessProfile {
  brandName: string;
  brandDescription: string;
  brandColor: string;
  brandLogoUrl: string;
  brandWebsite: string;
  brandPhone: string;
  verificationStatus: "unverified" | "pending" | "verified" | "suspended";
  agentId: string;
}

interface RcsPhoneStatus {
  integrationId: number;
  phoneNumber: string;
  rcsEnabled: boolean;
  rcsCapable: boolean;
  teamId: string;
  workflowName?: string;
}

interface RcsUsage {
  rcsThisMonth: number;
  maxRcsPerMonth: number | null;
  smsThisMonth: number;
  maxSmsPerMonth: number | null;
  rcsFallbackCount: number;
  rcsDeliveredCount: number;
  rcsReadCount: number;
  rcsFailedCount: number;
}

type Tab = "profile" | "numbers" | "delivery" | "settings";

function RcsLogo({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9z" />
    </svg>
  );
}

function VerificationBadge({ status }: { status: RcsBusinessProfile["verificationStatus"] }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    verified: { bg: "bg-green-100", text: "text-green-700", label: "Verified" },
    pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending Review" },
    unverified: { bg: "bg-slate-100", text: "text-slate-600", label: "Not Submitted" },
    suspended: { bg: "bg-red-100", text: "text-red-700", label: "Suspended" }
  };
  const c = config[status] ?? config.unverified;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

const DEFAULT_PROFILE: RcsBusinessProfile = {
  brandName: "",
  brandDescription: "",
  brandColor: "#4F46E5",
  brandLogoUrl: "",
  brandWebsite: "",
  brandPhone: "",
  verificationStatus: "unverified",
  agentId: ""
};

export default function RcsIntegration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<Tab>("profile");
  const [profile, setProfile] = useState<RcsBusinessProfile>(DEFAULT_PROFILE);
  const [phoneStatuses, setPhoneStatuses] = useState<RcsPhoneStatus[]>([]);
  const [usage, setUsage] = useState<RcsUsage | null>(null);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [fallbackEnabled, setFallbackEnabled] = useState(true);
  const api = useApi();
  const { getTeamNameById } = useTeams();

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.messaging.get("Rcs/Profile");
      if (response.ok) {
        const data = await response.json();
        setProfile({ ...DEFAULT_PROFILE, ...data });
      }
    } catch {
      // Profile may not exist yet — keep defaults
    }
  }, [api.messaging]);

  const fetchPhoneStatuses = useCallback(async () => {
    try {
      const response = await api.messaging.get("Rcs/PhoneStatuses");
      if (response.ok) {
        const data = await response.json();
        setPhoneStatuses(Array.isArray(data) ? data : []);
      }
    } catch {
      setPhoneStatuses([]);
    }
  }, [api.messaging]);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await api.messaging.get("Rcs/Usage");
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch {
      setUsage(null);
    }
  }, [api.messaging]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.messaging.get("Rcs/Settings");
      if (response.ok) {
        const data = await response.json();
        setFallbackEnabled(data?.smsFallbackEnabled ?? true);
      }
    } catch {
      // Keep defaults
    }
  }, [api.messaging]);

  useEffect(() => {
    Promise.all([fetchProfile(), fetchPhoneStatuses(), fetchUsage(), fetchSettings()])
      .finally(() => setLoading(false));
  }, [fetchProfile, fetchPhoneStatuses, fetchUsage, fetchSettings]);

  const saveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const resp = await api.messaging.post("Rcs/Profile", profile);
      if (resp.ok) {
        const data = await resp.json();
        setProfile({ ...DEFAULT_PROFILE, ...data });
        setMessage({ text: "RCS Business Profile saved successfully.", type: "success" });
      } else {
        setMessage({ text: "Failed to save RCS profile. Check all required fields.", type: "error" });
      }
    } catch {
      setMessage({ text: "Failed to save RCS profile.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const submitForVerification = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const resp = await api.messaging.post("Rcs/Profile/Submit", {});
      if (resp.ok) {
        setProfile(prev => ({ ...prev, verificationStatus: "pending" }));
        setMessage({ text: "RCS Business Profile submitted for Google verification. This typically takes 3-5 business days.", type: "success" });
      } else {
        setMessage({ text: "Failed to submit for verification. Ensure all profile fields are complete.", type: "error" });
      }
    } catch {
      setMessage({ text: "Submission failed.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const toggleRcsOnNumber = async (integrationId: number, enable: boolean) => {
    try {
      const resp = await api.messaging.post("Rcs/PhoneStatuses/Toggle", {
        integrationId,
        rcsEnabled: enable
      });
      if (resp.ok) {
        setPhoneStatuses(prev =>
          prev.map(p => p.integrationId === integrationId ? { ...p, rcsEnabled: enable } : p)
        );
      }
    } catch {
      setMessage({ text: "Failed to update RCS status on this number.", type: "error" });
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const resp = await api.messaging.post("Rcs/Settings", { smsFallbackEnabled: fallbackEnabled });
      if (resp.ok) {
        setMessage({ text: "RCS settings saved.", type: "success" });
      } else {
        setMessage({ text: "Failed to save settings.", type: "error" });
      }
    } catch {
      setMessage({ text: "Failed to save settings.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: "profile", label: "Business Profile", icon: "fa-building" },
    { key: "numbers", label: "Phone Numbers", icon: "fa-phone" },
    { key: "delivery", label: "Delivery Reports", icon: "fa-chart-bar" },
    { key: "settings", label: "Settings", icon: "fa-cog" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 text-white">
          <RcsLogo size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">RCS Business Messaging</h2>
          <p className="text-sm text-slate-500">
            Rich Communication Services — the next generation of SMS. Branded sender, read receipts,
            typing indicators, rich cards, carousels, and suggested replies. Uses your existing SMS
            phone numbers with automatic SMS fallback.
          </p>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Key benefits callout */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="text-sm text-indigo-900 font-semibold mb-2">Why RCS?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-indigo-800">
          <div className="flex items-start gap-2">
            <i className="fas fa-check-double text-indigo-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span><strong>Read receipts</strong> — know when messages are seen</span>
          </div>
          <div className="flex items-start gap-2">
            <i className="fas fa-building text-indigo-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span><strong>Branded sender</strong> — your logo, name &amp; color</span>
          </div>
          <div className="flex items-start gap-2">
            <i className="fas fa-images text-indigo-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span><strong>Rich cards</strong> — images, buttons, carousels</span>
          </div>
          <div className="flex items-start gap-2">
            <i className="fas fa-arrow-down text-indigo-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span><strong>SMS fallback</strong> — auto-falls back if RCS unavailable</span>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="RCS tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
              aria-label={t.label}
            >
              <i className={`fas ${t.icon} mr-2`} aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tab === "profile" && (
        <ProfileTab
          profile={profile}
          setProfile={setProfile}
          onSave={saveProfile}
          onSubmit={submitForVerification}
          saving={saving}
        />
      )}
      {tab === "numbers" && (
        <NumbersTab
          phoneStatuses={phoneStatuses}
          getTeamNameById={getTeamNameById}
          onToggle={toggleRcsOnNumber}
          profileVerified={profile.verificationStatus === "verified"}
        />
      )}
      {tab === "delivery" && (
        <DeliveryTab usage={usage} />
      )}
      {tab === "settings" && (
        <SettingsTab
          fallbackEnabled={fallbackEnabled}
          setFallbackEnabled={setFallbackEnabled}
          onSave={saveSettings}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Profile Tab ─────────────────────────────────────────────────────────────

function ProfileTab({
  profile,
  setProfile,
  onSave,
  onSubmit,
  saving
}: {
  profile: RcsBusinessProfile;
  setProfile: React.Dispatch<React.SetStateAction<RcsBusinessProfile>>;
  onSave: () => void;
  onSubmit: () => void;
  saving: boolean;
}) {
  const update = (field: keyof RcsBusinessProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">RCS Business Profile</h3>
          <VerificationBadge status={profile.verificationStatus} />
        </div>

        <p className="text-sm text-slate-500">
          Google requires a verified business profile before RCS messages can be sent. Your brand name,
          logo, and color appear in the recipient's messaging app alongside your messages.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Brand Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Your Company Name"
              value={profile.brandName}
              onChange={e => update("brandName", e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-slate-400 mt-1">Displayed as the sender name in RCS messages</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Brand Color <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="w-10 h-10 rounded border-slate-300 cursor-pointer"
                value={profile.brandColor}
                onChange={e => update("brandColor", e.target.value)}
              />
              <input
                type="text"
                className="w-28 rounded-md border-slate-300 shadow-sm text-sm font-mono focus:border-indigo-500 focus:ring-indigo-500"
                value={profile.brandColor}
                onChange={e => update("brandColor", e.target.value)}
                maxLength={7}
              />
              <span className="text-xs text-slate-400">Accent color in the chat bubble</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Brand Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Brief description of your business (shown to users when they tap your brand name)"
              value={profile.brandDescription}
              onChange={e => update("brandDescription", e.target.value)}
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-slate-400 mt-1">{profile.brandDescription.length}/500 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Logo URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              className="w-full rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="https://yoursite.com/logo.png"
              value={profile.brandLogoUrl}
              onChange={e => update("brandLogoUrl", e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">Square PNG/JPG, minimum 224x224px. Shown as avatar next to messages.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
            <input
              type="url"
              className="w-full rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="https://yourcompany.com"
              value={profile.brandWebsite}
              onChange={e => update("brandWebsite", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
            <input
              type="tel"
              className="w-full rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="+1 (555) 123-4567"
              value={profile.brandPhone}
              onChange={e => update("brandPhone", e.target.value)}
            />
          </div>

          {profile.agentId && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">RCS Agent ID</label>
              <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-600">
                {profile.agentId}
              </code>
              <p className="text-xs text-slate-400 mt-1">Assigned by Google after profile submission</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save Profile"}
            onClick={onSave}
            disabled={saving || !profile.brandName || !profile.brandDescription || !profile.brandLogoUrl}
          />
          {profile.verificationStatus === "unverified" && profile.brandName && profile.brandDescription && profile.brandLogoUrl && (
            <button
              className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors disabled:opacity-50"
              onClick={onSubmit}
              disabled={saving}
            >
              Submit for Google Verification
            </button>
          )}
        </div>
      </div>

      {/* Google verification requirements */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <i className="fas fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Google Verification Required:</span> RCS Business Messaging
          requires Google to verify your business identity before messages can be sent. This includes
          verifying your brand name, logo, website, and business legitimacy. The process typically takes
          3-5 business days. Until verified, RCS messages will fall back to SMS.
        </div>
      </div>

      {/* RCS vs SMS comparison */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Feature</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">SMS</th>
              <th className="px-4 py-3 text-center font-medium text-indigo-600">RCS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ["Branded sender (logo + name)", false, true],
              ["Read receipts", false, true],
              ["Typing indicators", false, true],
              ["Rich cards (image + buttons)", false, true],
              ["Carousels", false, true],
              ["Suggested replies (quick chips)", false, true],
              ["Media sharing (up to 100MB)", false, true],
              ["Delivery receipts", false, true],
              ["Character limit", "160/segment", "8,000"],
              ["Automatic SMS fallback", "N/A", true],
              ["Same phone numbers", true, true],
              ["Works on Android", true, true],
              ["Works on iPhone (iOS 18+)", true, true],
            ].map(([feature, sms, rcs], i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 text-slate-700">{feature}</td>
                <td className="px-4 py-2.5 text-center">
                  {typeof sms === "boolean" ? (
                    sms ? <i className="fas fa-check text-green-500" aria-hidden="true" /> : <i className="fas fa-xmark text-slate-300" aria-hidden="true" />
                  ) : (
                    <span className="text-slate-500 text-xs">{sms}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {typeof rcs === "boolean" ? (
                    rcs ? <i className="fas fa-check text-indigo-500" aria-hidden="true" /> : <i className="fas fa-xmark text-slate-300" aria-hidden="true" />
                  ) : (
                    <span className="text-indigo-600 text-xs font-medium">{rcs}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Numbers Tab ─────────────────────────────────────────────────────────────

function NumbersTab({
  phoneStatuses,
  getTeamNameById,
  onToggle,
  profileVerified
}: {
  phoneStatuses: RcsPhoneStatus[];
  getTeamNameById: (id: number) => string;
  onToggle: (integrationId: number, enable: boolean) => void;
  profileVerified: boolean;
}) {
  if (phoneStatuses.length === 0) {
    return (
      <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-3 text-indigo-600">
          <i className="fas fa-phone text-lg" aria-hidden="true" />
        </div>
        <p className="text-slate-600 font-medium">No SMS numbers found</p>
        <p className="text-slate-400 text-sm mt-1">
          RCS uses your existing SMS phone numbers. Go to{" "}
          <a href="/sms" className="text-indigo-600 hover:text-indigo-800 underline">
            Channels &rarr; SMS
          </a>{" "}
          to add a phone number first, then come back to enable RCS on it.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!profileVerified && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex gap-2">
          <i className="fas fa-info-circle text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>
            RCS is available on these numbers but your Business Profile is not yet verified by Google.
            Messages will fall back to SMS until verification is complete.
          </span>
        </div>
      )}

      <p className="text-sm text-slate-500">
        Enable or disable RCS on each of your SMS phone numbers. When RCS is enabled, outbound messages
        will attempt RCS delivery first and automatically fall back to SMS if the recipient's device or
        carrier doesn't support RCS.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Phone Number</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Workflow</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">RCS Capable</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">RCS Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {phoneStatuses.map(p => (
              <tr key={p.integrationId} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <span className="font-medium text-slate-800 font-mono">{p.phoneNumber}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {getTeamNameById(Number(p.teamId)) || "All teams"}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">
                  {p.workflowName ? (
                    <span className="inline-flex items-center gap-1">
                      <i className="fas fa-diagram-project text-blue-400" aria-hidden="true" />
                      {p.workflowName}
                    </span>
                  ) : (
                    <span className="text-slate-400">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {p.rcsCapable ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                      No
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={p.rcsEnabled}
                      onChange={() => onToggle(p.integrationId, !p.rcsEnabled)}
                      disabled={!p.rcsCapable}
                      aria-label={`Toggle RCS on ${p.phoneNumber}`}
                    />
                    <div className={`w-9 h-5 rounded-full peer-focus:ring-2 peer-focus:ring-indigo-300 transition-colors ${
                      p.rcsEnabled ? "bg-indigo-600" : "bg-slate-300"
                    } ${!p.rcsCapable ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        p.rcsEnabled ? "translate-x-4" : ""
                      }`} />
                    </div>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <i className="fas fa-circle-info text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-blue-800">
          <span className="font-semibold">RCS Capability:</span> Not all phone numbers support RCS.
          Short codes and some toll-free numbers may not be RCS-capable. Local 10DLC long-code numbers
          typically support RCS. If a number shows "No" for RCS Capable, it can still
          send and receive standard SMS.
        </div>
      </div>
    </div>
  );
}

// ── Delivery Tab ────────────────────────────────────────────────────────────

function DeliveryTab({ usage }: { usage: RcsUsage | null }) {
  if (!usage) {
    return (
      <div className="text-center py-10 text-slate-400 text-sm">
        No delivery data available yet. RCS delivery reports will appear here once you start sending messages.
      </div>
    );
  }

  const deliveryRate = usage.rcsDeliveredCount + usage.rcsReadCount > 0
    ? Math.round(((usage.rcsDeliveredCount + usage.rcsReadCount) / (usage.rcsDeliveredCount + usage.rcsReadCount + usage.rcsFailedCount)) * 100)
    : 0;

  const readRate = usage.rcsReadCount > 0
    ? Math.round((usage.rcsReadCount / (usage.rcsDeliveredCount + usage.rcsReadCount)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Quota bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">RCS messages this month</span>
          <span className="text-sm text-slate-500">
            {usage.rcsThisMonth.toLocaleString()}
            {usage.maxRcsPerMonth ? ` / ${usage.maxRcsPerMonth.toLocaleString()}` : " (included with plan)"}
          </span>
        </div>
        {usage.maxRcsPerMonth ? (
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                usage.rcsThisMonth / usage.maxRcsPerMonth >= 0.9
                  ? "bg-red-500"
                  : usage.rcsThisMonth / usage.maxRcsPerMonth >= 0.7
                  ? "bg-amber-400"
                  : "bg-indigo-500"
              }`}
              style={{ width: `${Math.min(100, (usage.rcsThisMonth / usage.maxRcsPerMonth) * 100)}%` }}
            />
          </div>
        ) : (
          <div className="w-full bg-indigo-100 rounded-full h-2">
            <div className="h-2 w-1/4 rounded-full bg-indigo-400" />
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">
          RCS messages also count toward your SMS monthly quota
          ({usage.smsThisMonth?.toLocaleString() ?? 0}
          {usage.maxSmsPerMonth ? ` / ${usage.maxSmsPerMonth.toLocaleString()}` : ""} total SMS+RCS).
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Delivered"
          value={usage.rcsDeliveredCount}
          icon="fa-check"
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          label="Read"
          value={usage.rcsReadCount}
          icon="fa-check-double"
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatCard
          label="Failed (Fell back to SMS)"
          value={usage.rcsFallbackCount}
          icon="fa-arrow-rotate-left"
          color="text-amber-600"
          bgColor="bg-amber-50"
        />
        <StatCard
          label="Failed (No fallback)"
          value={usage.rcsFailedCount}
          icon="fa-xmark"
          color="text-red-600"
          bgColor="bg-red-50"
        />
      </div>

      {/* Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-sm text-slate-500 mb-1">Delivery Rate</div>
          <div className="text-2xl font-bold text-slate-800">{deliveryRate}%</div>
          <p className="text-xs text-slate-400 mt-1">
            Percentage of RCS messages successfully delivered (delivered + read vs total attempts)
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-sm text-slate-500 mb-1">Read Rate</div>
          <div className="text-2xl font-bold text-indigo-600">{readRate}%</div>
          <p className="text-xs text-slate-400 mt-1">
            Percentage of delivered RCS messages that were opened and read by the recipient
          </p>
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-800">
        <i className="fas fa-lightbulb text-indigo-500 mr-2" aria-hidden="true" />
        <strong>Tip:</strong> RCS read rates are typically 3-5x higher than SMS because recipients see
        your brand name and logo, making them more likely to open and engage. Industry average RCS open
        rate is 80% compared to 25% for SMS.
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
  bgColor
}: {
  label: string;
  value: number;
  icon: string;
  color: string;
  bgColor: string;
}) {
  return (
    <div className={`${bgColor} rounded-lg p-4`}>
      <div className="flex items-center gap-2 mb-1">
        <i className={`fas ${icon} ${color}`} aria-hidden="true" />
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <div className="text-xl font-bold text-slate-800">{value.toLocaleString()}</div>
    </div>
  );
}

// ── Settings Tab ────────────────────────────────────────────────────────────

function SettingsTab({
  fallbackEnabled,
  setFallbackEnabled,
  onSave,
  saving
}: {
  fallbackEnabled: boolean;
  setFallbackEnabled: (v: boolean) => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <h3 className="text-base font-semibold text-slate-800">RCS Delivery Settings</h3>

        {/* SMS Fallback toggle */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <label className="relative inline-flex items-center cursor-pointer mt-1">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={fallbackEnabled}
              onChange={() => setFallbackEnabled(!fallbackEnabled)}
              aria-label="SMS fallback toggle"
            />
            <div className={`w-11 h-6 rounded-full peer-focus:ring-2 peer-focus:ring-indigo-300 transition-colors ${
              fallbackEnabled ? "bg-indigo-600" : "bg-slate-300"
            }`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                fallbackEnabled ? "translate-x-5" : ""
              }`} />
            </div>
          </label>
          <div>
            <div className="text-sm font-medium text-slate-700">Automatic SMS Fallback</div>
            <p className="text-sm text-slate-500 mt-1">
              When enabled, messages that can't be delivered via RCS (recipient doesn't support RCS,
              carrier not supported, or delivery timeout) will automatically be re-sent as a standard
              SMS. <strong>Highly recommended</strong> — disabling this means some recipients may not
              receive your messages at all.
            </p>
          </div>
        </div>

        {!fallbackEnabled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex gap-2">
            <i className="fas fa-exclamation-triangle text-red-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              <strong>Warning:</strong> SMS fallback is disabled. Recipients whose devices or carriers
              don't support RCS will NOT receive your messages. Only disable this if you're sure all
              your recipients support RCS.
            </span>
          </div>
        )}

        {/* Carrier support info */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Carrier &amp; Device Support</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="font-medium text-green-800 mb-1">
                <i className="fas fa-check-circle text-green-500 mr-1" aria-hidden="true" />
                Supported
              </div>
              <ul className="text-green-700 space-y-0.5 text-xs">
                <li>Android (Google Messages) — all carriers</li>
                <li>iPhone (iOS 18+ Messages app)</li>
                <li>AT&amp;T, T-Mobile, Verizon (US)</li>
                <li>Vodafone, Deutsche Telekom, Orange (EU)</li>
                <li>700+ carriers in 60+ countries</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="font-medium text-slate-700 mb-1">
                <i className="fas fa-info-circle text-slate-400 mr-1" aria-hidden="true" />
                Falls back to SMS
              </div>
              <ul className="text-slate-600 space-y-0.5 text-xs">
                <li>Older iPhones (pre iOS 18)</li>
                <li>Feature phones &amp; basic phones</li>
                <li>Wi-Fi only tablets without SIM</li>
                <li>Carriers not yet supporting RCS</li>
                <li>Users with RCS disabled in settings</li>
              </ul>
            </div>
          </div>
        </div>

        {/* RCS message types info */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Rich Message Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="font-medium text-slate-800 mb-1">
                <i className="fas fa-rectangle-list text-indigo-500 mr-1" aria-hidden="true" />
                Rich Cards
              </div>
              <p className="text-xs text-slate-500">
                Image + title + description + up to 4 action buttons (open URL, dial phone, map location).
                Perfect for product previews, order confirmations.
              </p>
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="font-medium text-slate-800 mb-1">
                <i className="fas fa-layer-group text-indigo-500 mr-1" aria-hidden="true" />
                Carousels
              </div>
              <p className="text-xs text-slate-500">
                Horizontally scrollable set of 2-10 rich cards. Great for product catalogs, service menus,
                FAQs. Each card has its own image and buttons.
              </p>
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="font-medium text-slate-800 mb-1">
                <i className="fas fa-reply-all text-indigo-500 mr-1" aria-hidden="true" />
                Suggested Replies
              </div>
              <p className="text-xs text-slate-500">
                Quick-action chips below the message (up to 11). User taps one to reply instantly.
                Supports text replies, URLs, and phone number dials.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save Settings"}
            onClick={onSave}
            disabled={saving}
          />
        </div>
      </div>

      {/* Billing note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
        <i className="fas fa-receipt text-slate-400 mr-2" aria-hidden="true" />
        <strong>Billing:</strong> RCS messages count toward your monthly SMS quota with no additional markup.
        Pricing is the same per-message rate as SMS in most regions. Some international carriers may apply
        a small premium for RCS delivery. Contact support for current pricing details.
      </div>
    </div>
  );
}
